import { useEffect } from "react";

type JsonLd = Record<string, unknown> | Record<string, unknown>[];

export interface DocumentMeta {
  title: string;
  description: string;
  keywords?: string;
  canonical?: string;
  image?: string;
  type?: string;
  jsonLd?: JsonLd;
}

const SITE_NAME = "吃瓜复盘局";
const DEFAULT_IMAGE =
  "https://readdy.ai/api/search-image?query=Warm%20editorial%20abstract%20composition%20of%20paper%20cards%20and%20timeline%20nodes%20on%20a%20cream%20and%20terracotta%20background%2C%20minimal%20flat%20style%2C%20soft%20grain%20texture%2C%20no%20readable%20text&width=1200&height=630&seq=cf-og-default&orientation=landscape";

function upsertMeta(attr: "name" | "property", key: string, content: string) {
  let element = document.head.querySelector(`meta[${attr}="${key}"]`);
  if (!element) {
    element = document.createElement("meta");
    element.setAttribute(attr, key);
    document.head.appendChild(element);
  }
  element.setAttribute("content", content);
}

function upsertLink(rel: string, href: string) {
  let element = document.head.querySelector(`link[rel="${rel}"]`);
  if (!element) {
    element = document.createElement("link");
    element.setAttribute("rel", rel);
    document.head.appendChild(element);
  }
  element.setAttribute("href", href);
}

export function useDocumentMeta({
  title,
  description,
  keywords,
  canonical,
  image,
  type = "website",
  jsonLd,
}: DocumentMeta) {
  useEffect(() => {
    document.title = title;
    upsertMeta("name", "description", description);
    if (keywords) {
      upsertMeta("name", "keywords", keywords);
    }
    upsertMeta("name", "robots", "index,follow,max-image-preview:large");
    upsertMeta("property", "og:site_name", SITE_NAME);
    upsertMeta("property", "og:title", title);
    upsertMeta("property", "og:description", description);
    upsertMeta("property", "og:type", type);
    upsertMeta("property", "og:image", image ?? DEFAULT_IMAGE);
    if (canonical) {
      upsertMeta("property", "og:url", canonical);
      upsertLink("canonical", canonical);
    }
    upsertMeta("name", "twitter:card", "summary_large_image");
    upsertMeta("name", "twitter:title", title);
    upsertMeta("name", "twitter:description", description);
    upsertMeta("name", "twitter:image", image ?? DEFAULT_IMAGE);

    const previous = document.getElementById("page-jsonld");
    if (previous) {
      previous.remove();
    }
    if (jsonLd) {
      const script = document.createElement("script");
      script.type = "application/ld+json";
      script.id = "page-jsonld";
      script.text = JSON.stringify(jsonLd);
      document.head.appendChild(script);
    }
  }, [title, description, keywords, canonical, image, type, JSON.stringify(jsonLd)]);
}